import { useQuery } from '@tanstack/react-query'
import { type ConfigArea } from '@omnea-app/authz'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'

const fiveMinutesMs = 5 * 60 * 1000

export const GetManagedConfigAreasQueryKey = 'GetManagedConfigAreas'

const EMPTY_AREAS: ReadonlySet<ConfigArea> = new Set()

export type ManagedConfigAreas = {
  /** undefined while loading; a fetch error resolves to the empty set (fail closed). */
  managedAreas: ReadonlySet<ConfigArea> | undefined
  /** Whether the user may administer permissions — gates the permissions settings pages. */
  canManagePermissions: boolean
}

/**
 * The config areas the current user can manage plus their permission-administration access, per
 * the permissions service (OpenFGA). Fail closed on error — gated tabs stay hidden rather than
 * falling back to role-based access.
 */
export const useManagedConfigAreas = (enabled: boolean): ManagedConfigAreas => {
  const { getToken, user } = useAuthenticatedUser()
  const { data, isError } = useQuery({
    queryKey: [GetManagedConfigAreasQueryKey, user.id],
    queryFn: async () => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data: response, error } = await permissionsClient.GET('/config-areas')
      throwIfApiError(error)
      return response
    },
    select: (response) => ({
      managedAreas: new Set(response.configAreas.filter(({ canManage }) => canManage).map(({ area }) => area)),
      canManagePermissions: response.canManagePermissions,
    }),
    staleTime: fiveMinutesMs,
    enabled,
  })

  if (!enabled) return { managedAreas: EMPTY_AREAS, canManagePermissions: false }
  // Prefer cached data: a failed background refetch keeps the last-known access instead of
  // yanking every gated tab. Fail closed only when the initial fetch itself failed.
  if (data != null) return { managedAreas: data.managedAreas, canManagePermissions: data.canManagePermissions }
  if (isError) return { managedAreas: EMPTY_AREAS, canManagePermissions: false }
  return { managedAreas: undefined, canManagePermissions: false }
}
